import React, { useEffect } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const CoreFeatures = () => {
    const features = [
        { title: 'Patient Registration' },
        { title: 'Pharmacy & Inventory' },
        { title: 'Patient Records' },
        { title: 'Billing & Invoicing' },
        { title: 'Doctor Management' },
        { title: 'Staff Management' },
        { title: 'Appointment Scheduling' }
    ];

    const [sliderRef, instanceRef] = useKeenSlider(
        {
            loop: true,
            mode: 'free-scroll',
            slides: {
                perView: 1.2,
                spacing: 16,
            },
            breakpoints: {
                '(min-width: 640px)': {
                    slides: { perView: 2.2, spacing: 20 },
                },
                '(min-width: 1024px)': {
                    slides: { perView: 2.3, spacing: 20 },
                },
            },
        },
        [
            (slider) => {
                let timeout;
                let mouseOver = false;
                function clearNextTimeout() {
                    clearTimeout(timeout);
                }
                function nextTimeout() {
                    clearTimeout(timeout);
                    if (mouseOver) return;
                    timeout = setTimeout(() => {
                        slider.next();
                    }, 3000);
                }
                slider.on('created', () => {
                    slider.container.addEventListener('mouseover', () => {
                        mouseOver = true;
                        clearNextTimeout();
                    });
                    slider.container.addEventListener('mouseout', () => {
                        mouseOver = false;
                        nextTimeout();
                    });
                    nextTimeout();
                });
                slider.on('dragStarted', clearNextTimeout);
                slider.on('animationEnded', nextTimeout);
                slider.on('updated', nextTimeout);
            },
        ]
    );

    return (
        <section className="w-full bg-white py-16 lg:py-24 overflow-hidden select-none">
            <div className="max-w-[1440px] w-full mx-auto px-6 lg:px-16">

                <h2 className="text-[20px] sm:text-[30px] font-sora font-[600] text-[#01386A] tracking-tight mb-3">
                    Core Features
                </h2>

                <p className="text-[14px] sm:text-[16px] text-[#49577A] font-[500] font-graphik leading-relaxed max-w-[531px] mb-12">
                    We focus on creating real, positive impact for our customers, the industries we work with, and the communities around us.
                </p>

                <div ref={sliderRef} className="keen-slider">
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            className="keen-slider__slide"
                        >
                            <div
                                className="group relative flex flex-col justify-between p-[20px] lg:p-[40px] min-h-[300px] bg-white text-[#01386A] border border-[#01386A45] hover:text-white hover:border-[#0A4373] transition-all duration-500 ease-out overflow-hidden shadow-xs cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-[#0A4373] transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out z-0" />

                                <div className="absolute bottom-0 left-0 w-full h-[4px] z-20">
                                    <div className="absolute bottom-0 left-0 h-full w-full bg-[#0A4373] scale-x-0 origin-left group-hover:scale-x-100 group-hover:bg-orange-500 transition-all duration-500 ease-out" />
                                </div>
                                <div className="absolute bottom-0 left-0 w-full h-[4px] z-10">
                                    <div className="absolute bottom-0 left-0 h-full w-full bg-[#0A4373] origin-left " />
                                </div>

                                <h3 className="text-[16px] lg:text-[20px] font-[600] tracking-tight leading-snug relative z-10 text-[#01386A] font-sora group-hover:text-white transition-colors duration-500">
                                    {feature.title}
                                </h3>

                                <p className="text-[14px] lg:text-[16px] font-graphik font-[500] leading-relaxed relative z-10 text-[#49577A] group-hover:text-gray-200 transition-colors duration-500">
                                    Flexible work arrangements can be formal (which may be part of your contract) or informal (which you'll arrange with your lead).
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex items-center gap-3 mt-8">
                    <button
                        onClick={() => instanceRef.current?.prev()}
                        className="w-8 h-8 rounded-full border border-orange-500 flex items-center justify-center text-orange-500 hover:bg-orange-50 transition-colors"
                    >
                        <ArrowLeft size={16} />
                    </button>

                    <button
                        onClick={() => instanceRef.current?.next()}
                        className="w-8 h-8 rounded-full border border-orange-500 flex items-center justify-center text-orange-500 hover:bg-orange-50 transition-colors"
                    >
                        <ArrowRight size={16} />
                    </button>
                </div>

            </div>
        </section>
    );
};

export default CoreFeatures;